import type { ReactNode } from 'react';
import { useCallback, useRef } from 'react';
import type { FieldMetadata, ObjectMetadata } from '../../../data/client';
import { useRecord } from '../../../data/hooks';
import { Badge, cx, Skeleton, Tooltip } from '../../../ui';
import { MOTION, useMotion } from '../../../ui/motion';
import { ArchivedRowAction } from './ArchivedRowAction';
import { useOpenRecord } from './openRecord';
import { RecordRowMenu } from './RecordRowMenu';
import { RecordTableCell } from './RecordTableCell';
import { useRowSelection } from './RowSelectionProvider';
import { recordTitle, type TableColumn } from './tableModel';
import { useClippedText } from './useClippedText';

/**
 * ONE ROW, AND SINCE WAVE 3 IT IS A SELECTABLE ROW RATHER THAN A LINK WITH EDITORS IN IT
 * (L-B1, owner's "Ja" of 2026-09-10 23:10). A click selects, Cmd and Shift extend, a double
 * click or Enter opens, the name is text and every cell shows the arrow cursor; the model is
 * `rowSelection.ts` and the gestures are AppKit's
 * (doc: https://developer.apple.com/documentation/appkit/nstableview/doubleaction).
 *
 * Resolved from the CANONICAL cache key (INV-13). The list query holds ids only, so a write
 * to one record re-renders one row - which is what keeps the table still while the detail
 * pane beside it edits the record the row is about (round 3, R3-3).
 */
export interface RecordTableRowProps {
  readonly object: ObjectMetadata;
  readonly columns: readonly TableColumn[];
  /**
   * THE FIELDS THIS VIEW SHOWS, which is where the row menu's stage submenu comes from
   * (round 2, finding F2). It was EVERY field of the object, and the reasoning - a menu must
   * not lose an item because somebody hid a column - produced "Set Language" on a view whose
   * columns are Name, Status, CSM and revenue: the object's first SELECT by position rather
   * than the one on screen. What the reader is looking at is the right scope.
   */
  readonly fields: readonly FieldMetadata[];
  /** The view's board field: what "stage" means on this object, for the row menu. */
  readonly boardGroupFieldId: string | null;
  readonly recordId: string;
  readonly present?: boolean;
  readonly onExited?: () => void;
  /** D21: this row is a tombstone - state it, and offer the way out. */
  readonly archived?: boolean | undefined;
  /**
   * THE DRAG HANDLE, when the table is grouped (group-by-aggregation.md D24). It sits
   * inside the title cell rather than in a gutter column of its own, so the grouped table
   * and the flat one keep the same `<colgroup>` and a number in the footer cannot drift
   * out from under its column.
   *
   * Absent everywhere else, and the title cell then renders exactly what it always did:
   * the ungrouped table has nowhere to drop a row TO, so a handle there would be an
   * affordance for a gesture with no destination.
   */
  readonly leading?: ReactNode | undefined;
}

export function RecordTableRow({
  object,
  columns,
  fields,
  boardGroupFieldId,
  recordId,
  present = true,
  onExited,
  archived = false,
  leading,
}: RecordTableRowProps) {
  const record = useRecord(object.id, recordId);
  const selection = useRowSelection();
  const open = useOpenRecord(object.id);
  const retained = useRef(record.data);
  if (record.data != null) retained.current = record.data;
  const row = present ? record.data : retained.current;
  const motion = useMotion(present, { onExited });
  /**
   * A CUT-OFF VALUE CARRIES ITS FULL TEXT ON THE HELP TAG (R6 A9, S2), in the column head's
   * own construction (`TableHead`, C6-3): the row asks which of its values do not fit their
   * column, and only those are wrapped in the product's `Tooltip`.
   */
  const rowElement = useRef<HTMLTableRowElement | null>(null);
  const motionRef = motion.ref;
  const setRowRef = useCallback(
    (node: HTMLTableRowElement | null) => {
      rowElement.current = node;
      motionRef(node);
    },
    [motionRef],
  );
  const clipped = useClippedText(rowElement, 'data-cell-value', present);
  const selected = selection.isSelected(recordId);
  /**
   * The menu acts on the whole selection when this row is in it, and on this row alone
   * otherwise - resolved HERE rather than inside the menu, because the answer is a
   * property of the row that was clicked.
   */
  const targetIds = selected ? selection.selection.ids : [recordId];

  // `null` means the record is gone (deleted elsewhere); both cases keep the
  // table's geometry instead of collapsing the row to nothing.
  if (!motion.mounted) return null;

  if (row === undefined || row === null) {
    return <PlaceholderRow columns={columns} />;
  }

  const title = recordTitle(row, object.labelIdentifierFieldId);
  const titleFieldId = columns.find((column) => column.isTitle)?.field.id ?? '';

  /**
   * THE NAME IS TEXT (wave 3, L-B1; owner's "Ja" of 2026-09-10 23:10).
   *
   * It was a link, and a link is what made the row misbehave: Chrome owns Cmd-click and
   * Shift-click on an anchor, so the two gestures AppKit uses to extend a selection opened a
   * new tab and a new window instead ("Diese CMD und Shift funktionen klappen nicht weil
   * Chrome selber welche hat"), and a plain click both navigated and selected, which is the
   * "rechts auf oeffnet und gleichzeitig diese view" the owner saw. A Mac table's first
   * column is a label; opening is the DOUBLE click, Enter, or the row menu's Open
   * (doc: https://developer.apple.com/documentation/appkit/nstableview/doubleaction).
   *
   * No `hover:underline` either: an underline is a link's promise, and this is not one.
   */
  const titleSpan = (
    <span
      data-cell-value={titleFieldId}
      className={cx(
        'block truncate',
        /**
         * DIMMED, in the one way that keeps criterion 2's 4.5:1 floor: the muted
         * INK token, not an opacity wash over the row. Washing the row would drop
         * every value below the contrast floor at exactly the moment the reader has
         * to check whether this is the record they meant to bring back.
         */
        archived ? 'text-ink-muted' : 'text-ink',
      )}
    >
      {title}
    </span>
  );
  const clippedTitle = clipped.get(titleFieldId);
  const titleText =
    clippedTitle === undefined ? titleSpan : <Tooltip label={clippedTitle}>{titleSpan}</Tooltip>;

  /** doc: docs/decisions/motion.md section 2, selection is immediate.
   * The pointer-tier background transition also delayed selection, so selectable rows
   * use the shell's fill states directly. MOTION.row owns only insertion and removal.
   */
  return (
    <RecordRowMenu
      object={object}
      fields={fields}
      targetIds={targetIds}
      archived={archived}
      boardGroupFieldId={boardGroupFieldId}
      onOpen={() => selection.contextClick(recordId)}
    >
      <tr
        ref={setRowRef}
        data-state={motion.state}
        inert={!present ? true : undefined}
        // `cursor-default`: the arrow, everywhere on the row (L-B1, and the product-wide
        // rule SH-A13). The cells declare it too, because a `<td>`'s own cursor wins.
        className={cx('shell-table-row cursor-default', MOTION.row)}
        aria-selected={selected}
        /**
         * THE ROW IS FOCUSABLE AND NAMES ITSELF, because the arrow keys move a selection
         * and the row that gains it has to gain the FOCUS with it: the emphasized fill is
         * `table:focus-within` (S1), so a selection driven from the scroll region alone
         * would paint the unemphasized grey while the reader is standing in the table.
         * `RowSelectionProvider.move` focuses the row by this attribute.
         */
        data-row-id={recordId}
        tabIndex={-1}
        /**
         * CAPTURE, so a click that lands in a cell still selects the row it landed in -
         * which is what a Mac table does and what makes "click a cell, then act on the
         * row" one gesture instead of two.
         *
         * CONTROLS KEEP THEIR OWN CLICK, and with the title no longer a link that rule has
         * one meaning instead of two: the spec's section 5 read "Cmd-click a row opens the
         * page in a new tab" against section 2's "Cmd-click ADDS to the selection", and the
         * contradiction only existed because the row was an anchor. Cmd-click anywhere on
         * the row is AppKit's add now (L-B1); the row's own controls - the trailing jump
         * icon, an archived row's Restore, the phone's chevron - keep theirs.
         */
        onPointerDownCapture={(event) => {
          /**
           * THE PRIMARY BUTTON ONLY. A secondary click must NOT replace the selection: the
           * menu it opens acts on what is selected when the row is part of it, and a
           * capture handler that had already replaced the selection would have made that
           * rule unreachable - right-clicking one of three selected rows would archive one.
           * `RecordRowMenu`'s `onOpen` is what selects the row when it is not in the
           * selection, which is the macOS rule stated once.
           */
          if (event.button !== 0) return;
          const control =
            event.target instanceof Element
              ? event.target.closest('a,button,input,select,textarea')
              : null;
          if (control !== null) return;
          selection.click(recordId, event);
          event.currentTarget.focus();
        }}
        /**
         * THE SECOND CLICK ACTS (L-B1). `NSTableView` has exactly this pair - `action` on a
         * click and `doubleAction` on a double click - and Finder, Mail and Notes all open on
         * the second one (doc:
         * https://developer.apple.com/documentation/appkit/nstableview/doubleaction).
         *
         * NOT ON A CONTROL, for the reason the capture handler gives: a double click that
         * lands on the jump icon or on Restore is that control's gesture twice, not the row's.
         */
        onDoubleClick={(event) => {
          const control =
            event.target instanceof Element
              ? event.target.closest('a,button,input,select,textarea')
              : null;
          if (control !== null) return;
          open(recordId);
        }}
        /**
         * THE KEYBOARD'S HALF OF THE SAME TWO GESTURES (spec/screens/list.md section 5).
         * Enter opens the focused row, which is what Return does in a Finder window, and
         * Space adds or removes it from the selection - a toggle, so `selection.click` is
         * called with the Cmd meaning rather than with a second code path.
         *
         * The arrows, Cmd-A and Escape are heard one level up, on the scroll region
         * (`TableSurface`), because they are questions about the TABLE rather than about this
         * row; they reach it by bubbling from here.
         */
        onKeyDown={(event) => {
          const control =
            event.target instanceof Element && event.target !== event.currentTarget
              ? event.target.closest('a,button,input,select,textarea')
              : null;
          if (control !== null) return;
          if (event.key === 'Enter') {
            event.preventDefault();
            open(recordId);
            return;
          }
          if (event.key === ' ') {
            // Without this the page scrolls a screen down under the row it just toggled.
            event.preventDefault();
            selection.click(recordId, { shiftKey: false, metaKey: true, ctrlKey: false });
          }
        }}
      >
        {columns.map((column) =>
          column.isTitle ? (
            <th
              key={column.field.id}
              scope="row"
              className="data-cell border-b border-line-subtle text-left align-middle font-medium"
            >
              {/*
              ONE LINE, AND THE ROW KEEPS ITS HEIGHT (audit P1-5). The badge and the exit used
              to be appended UNDER the name in a wrapping block, which made a tombstone 119px
              tall - 127px when "Undo the merge" wrapped - beside 54px rows, so a row's height
              became a function of its state and of its copy. They sit beside the name now,
              they never wrap, and the column carries the extra width they need
              (`archivedColumns`) instead of the row carrying extra height.
            */}
              <div className="flex min-w-0 items-center gap-2">
                {leading}
                <span className="min-w-0 flex-1">{titleText}</span>
                {archived && (
                  <>
                    <Badge tone="neutral" size="sm">
                      Archived
                    </Badge>
                    <ArchivedRowAction
                      compact
                      objectId={object.id}
                      recordId={recordId}
                      title={title}
                    />
                  </>
                )}
              </div>
            </th>
          ) : (
            <RecordTableCell
              key={column.field.id}
              objectId={object.id}
              record={row}
              column={column}
              rowTitle={title}
              clippedText={clipped.get(column.field.id)}
            />
          ),
        )}
      </tr>
    </RecordRowMenu>
  );
}

/**
 * A row-shaped loading placeholder, shared with the table's own loading state. The
 * Skeletons inside are already hidden from assistive technology; the container
 * carries `aria-busy`, so nothing here needs `aria-hidden`.
 */
export function PlaceholderRow({ columns }: { readonly columns: readonly TableColumn[] }) {
  return (
    <tr>
      {columns.map((column) => (
        <td
          key={column.field.id}
          className="data-cell h-(--row-height) border-b border-line-subtle"
        >
          <Skeleton className="h-3 w-2/3" />
        </td>
      ))}
    </tr>
  );
}
